/**
 * Truck-first receiving.
 *
 * A vendor shipment record is one truck's worth of one order: Marvin reports
 * every package on the truck with the tracking number the dock scans. The
 * receiver does not pick POs before unloading; the first package scanned names
 * the truck, and everything else on that truck is what the session expects.
 * These helpers are pure so the rules can be pinned without mounting a page.
 */

import { sortBy } from "lodash";
import { codesMatch } from "./barcode";
import { normalizeVendorOrder } from "./vendorOrderNumber";
import { quakerUnitKey } from "./vendorBarcode";

/**
 * Marvin's placeholder for a load that has not been put on a truck yet is
 * truck "0". A session must never lock onto it, or the next real truck's
 * packages would all be refused as "another truck".
 */
export function isRealTruck(truckNumber) {
  const truck = String(truckNumber == null ? "" : truckNumber).trim();
  return Boolean(truck) && truck !== "0";
}

/** Every shipment record for one truck, across all of its orders. */
export function shipmentsOnTruck(shipments, truckNumber) {
  if (!isRealTruck(truckNumber)) return [];
  const truck = String(truckNumber).trim();
  return shipments.filter(
    (shipment) => String(shipment.data.truckNumber || "").trim() === truck
  );
}

/** A package's key across records: its order and its tracking number. */
function packageKey(orderID, trackingNo) {
  return `${orderID}|${String(trackingNo == null ? "" : trackingNo).trim()}`;
}

/**
 * The package with this tracking number on one record, as
 * `{ line, trackingLine }`, or null. A scan records the package's own
 * tracking number, so an exact match is the right one.
 */
function packageOn(shipment, trackingNo) {
  const wanted = String(trackingNo == null ? "" : trackingNo).trim();
  if (!shipment || !wanted) return null;
  for (const line of shipment.data.lines || []) {
    for (const trackingLine of line.trackingLines || []) {
      if (trackingLine && String(trackingLine.trackingNo).trim() === wanted) {
        return { line, trackingLine };
      }
    }
  }
  return null;
}

/**
 * Of the shipment records that carry one scanned package, the one to receive
 * it against: a real truck's over Marvin's placeholder, then one of the
 * session's chosen orders, then the record synced most recently. A load
 * Marvin moves onto a truck can leave its placeholder copy behind, packages
 * and all, until the sync retires it -- and on Justin's dock (2026-10-05)
 * that stale copy came first, so every package of truck 2248835 was received
 * against it and the truck was never named. The real truck wins however the
 * two were synced: a later sync or backfill can touch the placeholder, and a
 * record written without `lastSynced` counts as never synced. A move from one
 * real truck to another can leave the old copy the same way; it stops being
 * synced, so the newer one wins. Null when there are none.
 */
export function preferredShipment(shipments = [], chosenOrderIDs = []) {
  const chosen = new Set(chosenOrderIDs);
  return (
    sortBy(shipments.filter(Boolean), [
      (shipment) => (isRealTruck(shipment.data.truckNumber) ? 0 : 1),
      (shipment) => (chosen.has(shipment.orderID) ? 0 : 1),
      (shipment) => -millis(shipment.data.lastSynced),
    ])[0] || null
  );
}

/**
 * Where a scanned package is received, given the records that carry it
 * (`hits`), as `{ shipment, otherTruck }`. Before a truck is named, the
 * preferred record (preferredShipment). Once one is, that truck's record when
 * it has one -- a stale copy elsewhere never turns its package away -- and
 * otherwise nothing when a real truck has it: `otherTruck` names that truck,
 * so the dock is told where the package belongs rather than shown an unknown
 * carton. A package only Marvin's placeholder lists is received against it
 * either way: the placeholder is not another truck, and the feed can lag the
 * load.
 */
export function shipmentForPackage(
  hits = [],
  truckNumber,
  chosenOrderIDs = []
) {
  const best = preferredShipment(hits, chosenOrderIDs);
  if (!isRealTruck(truckNumber)) return { shipment: best, otherTruck: null };
  const onTruck = preferredShipment(
    shipmentsOnTruck(hits.filter(Boolean), truckNumber),
    chosenOrderIDs
  );
  if (onTruck) return { shipment: onTruck, otherTruck: null };
  if (best && isRealTruck(best.data.truckNumber)) {
    return { shipment: null, otherTruck: String(best.data.truckNumber).trim() };
  }
  return { shipment: best, otherTruck: null };
}

/**
 * Whether a package was received on any record of its order. One package can
 * be on two records -- the copy a load leaves on Marvin's placeholder truck
 * when it moves onto a real one, until the sync retires it and carries its
 * stamps over -- and only the copy it was received against carries the
 * stamp. The real truck's copy is the one received (preferredShipment), so
 * asking that copy alone received a second time a package an earlier session
 * had received against the placeholder.
 */
export function packageReceived(shipments = [], orderID, trackingNo) {
  return shipments.some((shipment) => {
    if (!shipment || shipment.orderID !== orderID) return false;
    const found = packageOn(shipment, trackingNo);
    return Boolean(found && found.trackingLine.receivedAt);
  });
}

/**
 * The packages received on any of these records, as order-and-tracking-number
 * keys for outstandingPackages. A package received against one copy of a
 * load -- Marvin's placeholder, before the sync retires it and carries the
 * stamp over -- is unstamped on the real truck's copy until then.
 */
export function receivedPackages(shipments = []) {
  const received = new Set();
  shipments.forEach((shipment) => {
    if (!shipment) return;
    (shipment.data.lines || []).forEach((line) =>
      (line.trackingLines || []).forEach((trackingLine) => {
        if (
          trackingLine &&
          trackingLine.trackingNo &&
          trackingLine.receivedAt
        ) {
          received.add(packageKey(shipment.orderID, trackingLine.trackingNo));
        }
      })
    );
  });
  return received;
}

/**
 * The packages a session still expects: every tracking line on the given
 * shipments that the vendor has not marked received and this session has not
 * scanned. Indices are kept so a scan of one can be recorded against it.
 * A shipment whose order was received some other way (packing slip, QC
 * bypass, the super's Fully Received) expects nothing: its packages are not
 * listed and Complete does not report them missing. Nor is a package
 * `received` names (receivedPackages): it was received against another
 * record of its order, and listing it on this one asked the dock for it
 * again and called it missing at Complete.
 */
export function outstandingPackages(
  shipments,
  scannedBarcodes = new Set(),
  received = new Set()
) {
  const packages = [];
  shipments.forEach((shipment) => {
    if (shipment.data.orderReceived) return;
    (shipment.data.lines || []).forEach((line, lineIndex) => {
      (line.trackingLines || []).forEach((trackingLine, trackingIndex) => {
        // A package with no tracking number is the remnant of a shipment
        // record the array-index bug truncated (VendorShipment
        // .normalizeShipmentLines): nothing can be scanned against it, so it
        // must not sit in the Expected list as a nameless row.
        if (!trackingLine || !trackingLine.trackingNo) return;
        if (trackingLine.receivedAt) return;
        if (scannedBarcodes.has(String(trackingLine.trackingNo).trim())) return;
        if (
          received.has(packageKey(shipment.orderID, trackingLine.trackingNo))
        ) {
          return;
        }
        packages.push({
          shipment,
          line,
          trackingLine,
          lineIndex,
          trackingIndex,
          projectID: shipment.projectID,
          saleID: shipment.saleID,
          orderID: shipment.orderID,
          trackingNo: trackingLine.trackingNo,
          description: line.description,
          characterCode: line.characterCode,
          // Denormalized by the sync; "" on documents older than the
          // outstanding backfill.
          vendor: shipment.data.vendor || "",
          orderPO: shipment.data.orderPO || "",
        });
      });
    });
  });
  return packages;
}

/**
 * Group packages by the order they belong to, in a shape the lists can
 * render: `{ orderID, projectID, saleID, orderPO, vendor, packages }`.
 * The PO and vendor come from the session's order when it has one, else the
 * shipment's denormalized copy, else the order doc, else the sale number.
 */
export function groupPackagesByOrder(
  packages,
  selectedOrders = [],
  orders = {}
) {
  const groups = {};
  packages.forEach((pkg) => {
    if (!groups[pkg.orderID]) {
      const po = selectedOrders.find((o) => o.orderID === pkg.orderID);
      const order = orders[pkg.orderID];
      groups[pkg.orderID] = {
        orderID: pkg.orderID,
        projectID: pkg.projectID,
        saleID: pkg.saleID,
        orderPO:
          (po && po.orderPO) ||
          pkg.orderPO ||
          (order && order.data.orderPO) ||
          pkg.projectID + pkg.saleID,
        vendor:
          (po && po.vendor) || pkg.vendor || (order && order.data.vendor) || "",
        packages: [],
      };
    }
    groups[pkg.orderID].packages.push(pkg);
  });
  return Object.values(groups).sort((a, b) =>
    a.orderPO.localeCompare(b.orderPO)
  );
}

/**
 * The session entries for every order that has packages on a truck, so the
 * moment a truck is identified the whole load is expected. `existing` entries
 * are kept as they are; only orders the session does not have yet are added.
 * Orders whose packages have all been received already are left out -- a
 * truck can carry a PO that a previous session finished, or one that was
 * received by packing slip without being scanned.
 */
export function ordersOnTruck(
  shipments,
  truckNumber,
  existing = [],
  orders = {}
) {
  const added = [];
  const seen = new Set(existing.map((po) => po.orderID));
  shipmentsOnTruck(shipments, truckNumber).forEach((shipment) => {
    if (seen.has(shipment.orderID)) return;
    if (shipment.data.orderReceived) return;
    if (shipment.totalTracked <= shipment.totalReceived) return;
    seen.add(shipment.orderID);
    added.push(
      vendorOrderEntry(shipment, shipment.data, orders[shipment.orderID])
    );
  });
  return added;
}

/**
 * The session entry for an order the vendor's feed placed. `ids` carries its
 * projectID, saleID and orderID; `data` is a shipment record's data.
 */
function vendorOrderEntry(ids, data = {}, order = null) {
  return {
    projectID: ids.projectID,
    saleID: ids.saleID,
    orderID: ids.orderID,
    // The sync denormalizes vendor/orderPO onto the shipment; the order doc
    // and the sale number remain the fallback for pre-backfill documents.
    vendor: data.vendor || (order && order.data.vendor) || "",
    orderPO:
      data.orderPO ||
      (order && order.data.orderPO) ||
      ids.projectID + ids.saleID,
    ackID: null,
    source: "vendor",
    location: null,
    packingSlipDocument: null,
    packingSlipFile: null,
    missingDocument: false,
    packingSlipID: null,
    linkedOrderID: null,
    importedItems: [],
    isImporting: false,
  };
}

/** A scan that received one of the vendor's own packages. */
function isVendorScan(scan) {
  return Boolean(scan && scan.matched && scan.vendorShipmentID && scan.orderID);
}

/**
 * Session entries for the orders of vendor packages a session scanned but
 * does not list: without one, Complete saves no packing slip for their scans
 * ("Finish anyway" leaves them out). A package Marvin still listed on its
 * placeholder truck named no truck and added no PO, and Justin's dock
 * finished 25 such scans on no PO (2026-10-05); this is what puts a session
 * like that right. Each order once, labelled the way a truck's orders are --
 * from the record the scan names, else another of the order's, else the
 * order doc, else the sale number -- and flagged when the vendor's records
 * say it was received before this truck, so Complete saves it no second
 * slip. That flag is on every record of the order, so an order none of whose
 * records is in view gets no entry: nothing says whether it was received,
 * and a helper's device that guessed "not" put a second slip on the truck
 * (the page reads the order's records first; see shipmentsToRead). A scan
 * filed to a PO by hand names no shipment and cannot say what its entry was,
 * so it is left out.
 */
export function ordersForScans(
  scans = [],
  existing = [],
  shipments = [],
  orders = {}
) {
  const added = [];
  const seen = new Set(existing.map((po) => po.orderID));
  scans.forEach((scan) => {
    if (!isVendorScan(scan) || !scan.projectID || !scan.saleID) return;
    if (seen.has(scan.orderID)) return;
    const own = shipments.filter(
      (shipment) => shipment && shipment.orderID === scan.orderID
    );
    if (!own.length) return;
    seen.add(scan.orderID);
    const { data } =
      own.find((candidate) => candidate.id === scan.vendorShipmentID) || own[0];
    added.push({
      ...vendorOrderEntry(scan, data, orders[scan.orderID]),
      ...(data.orderReceived ? { orderReceived: true } : {}),
    });
  });
  return added;
}

/**
 * The truck a session's vendor packages came off, read from the feed when
 * nobody named one: where each package scanned is now, by its order and
 * tracking number (preferredShipment, when several records carry it), if
 * that is the same real truck for all of them. Null when none is on a real
 * truck -- only on Marvin's placeholder -- or they are on more than one.
 */
export function truckOfScans(scans = [], shipments = []) {
  const scanned = new Set(
    scans
      .filter(isVendorScan)
      .map((scan) => packageKey(scan.orderID, scan.barcode))
  );
  if (!scanned.size) return null;
  const carrying = new Map();
  shipments.forEach((shipment) => {
    if (!shipment) return;
    (shipment.data.lines || []).forEach((line) =>
      (line.trackingLines || []).forEach((trackingLine) => {
        if (!trackingLine) return;
        const found = packageKey(shipment.orderID, trackingLine.trackingNo);
        if (!scanned.has(found)) return;
        if (!carrying.has(found)) carrying.set(found, []);
        carrying.get(found).push(shipment);
      })
    );
  });
  const trucks = new Set();
  carrying.forEach((records) => {
    const truck = preferredShipment(records).data.truckNumber;
    if (isRealTruck(truck)) trucks.add(String(truck).trim());
  });
  return trucks.size === 1 ? [...trucks][0] : null;
}

/**
 * A session's vendor scans whose package no record in view carries. The
 * page's view of the vendor's records is the pending ones (outstanding > 0)
 * and the named truck's, so a record drops out of it once all its packages
 * are received -- a placeholder copy received in full, or the real truck's
 * copy once the sync retired the placeholder and carried its stamps over
 * (2026-10-05) -- and with it went the truck those scans were on, the sale
 * item each received, and whether its order was received before this truck.
 * The page reads these scans' orders' records again (shipmentsToRead).
 */
export function unseenScans(scans = [], shipments = []) {
  const carried = new Set();
  shipments.forEach((shipment) => {
    if (!shipment) return;
    (shipment.data.lines || []).forEach((line) =>
      (line.trackingLines || []).forEach((trackingLine) => {
        if (trackingLine) {
          carried.add(packageKey(shipment.orderID, trackingLine.trackingNo));
        }
      })
    );
  });
  return scans.filter(
    (scan) =>
      isVendorScan(scan) && !carried.has(packageKey(scan.orderID, scan.barcode))
  );
}

/**
 * The records of one order that say what a session's truck brought of it,
 * for Complete's expected and missing counts. With a truck named, that
 * truck's records. With none, the records carrying a package the session's
 * scans received: a record carrying none of them is another delivery -- had
 * this been its truck, a scan of its packages would have named it. Counting
 * every record of the order called the packages of an order's next truck
 * missing, and opened a vendor-billable issue for them, once a session took
 * its PO from scans of Marvin's placeholder copy (2026-10-05). An order none
 * of whose scans received a vendor package (a PO chosen up front, its cartons
 * filed by hand) still counts every record, as before.
 */
export function deliveryShipments(
  shipments = [],
  orderID,
  truckNumber,
  scans = []
) {
  const own = shipments.filter(
    (shipment) => shipment && shipment.orderID === orderID
  );
  if (truckNumber) return shipmentsOnTruck(own, truckNumber);
  const received = scans.filter(
    (scan) => isVendorScan(scan) && scan.orderID === orderID
  );
  if (!received.length) return own;
  return own.filter((shipment) =>
    received.some((scan) => packageOn(shipment, scan.barcode))
  );
}

/**
 * The acknowledgement a receiving packing slip is saved against: the order's
 * ack whose vendor order number the truck's packages came under, else the
 * order's earliest ack, else null. An order can carry two (a reorder, a
 * second vendor number), and the slip should name the one that shipped.
 */
export function receivingAck(acks, vendorOrderNumbers = []) {
  const list = (acks || []).filter(Boolean);
  const wanted = new Set(
    vendorOrderNumbers.map(normalizeVendorOrder).filter(Boolean)
  );
  const named = list.find((ack) =>
    wanted.has(normalizeVendorOrder(ack.data.vendorOrderNumber))
  );
  if (named) return named;
  const byCreated = sortBy(list, (ack) =>
    ack.data.created ? new Date(ack.data.created).getTime() : Infinity
  );
  return byCreated[0] || null;
}

/**
 * What a new session inherits when it continues a truck that was already
 * marked done: the truck, its vendor and its POs. Scans are not copied -- the
 * vendor records already say what the first pass received, so the Expected
 * list picks up exactly where it left off -- and the first pass's packing
 * slips stand; completing the continuation adds a slip for only what it
 * received. The link back is kept for the record.
 */
export function continuationOf(session) {
  const data = (session && session.data) || {};
  return {
    truckNumber: data.truckNumber || null,
    vendor: data.vendor || "",
    selectedOrders: (data.selectedOrders || []).map((po) => ({
      ...po,
      importedItems: po.importedItems || [],
    })),
    resumedFrom: session.id,
  };
}

/**
 * A session's POs with each of `more` they lack, by orderID, appended. What
 * is already there is kept as it is: several devices add to one truck's
 * list, and none may drop or rewrite an entry another put there. The same
 * array comes back when nothing was added.
 */
export function mergeSessionOrders(orders = [], more = []) {
  const have = new Set(orders.map((po) => po.orderID));
  const added = more.filter((po) => {
    if (have.has(po.orderID)) return false;
    have.add(po.orderID);
    return true;
  });
  return added.length ? [...orders, ...added] : orders;
}

/**
 * Session entries for the orders Marvin lists on a truck that have no home
 * in the app -- a PO nobody can read, or a sale with no vendor order -- so
 * their packages can still be taken in, as unassigned material, the way
 * "Add unassigned PO" does by hand. One entry per order, labelled with the
 * vendor's order number and job so the receiver knows what it is; orders
 * the session already lists (by that number) are left alone.
 *
 * `unplaced` is the callable's list: `{ orderNum, po, jobName, customerName,
 * packages }`. `note` renders the entry's notes from one of them.
 */
export function unassignedEntriesForUnplaced(
  unplaced,
  {
    truckNumber,
    existing = [],
    vendor = "Marvin",
    note = () => "",
    now = Date.now(),
  }
) {
  const seen = new Set(
    existing
      .map((po) =>
        String(po.vendorOrderNumber || "")
          .trim()
          .toUpperCase()
      )
      .filter(Boolean)
  );
  const entries = [];
  (unplaced || []).forEach((order) => {
    const orderNum = String(order.orderNum || "").trim();
    if (!orderNum || seen.has(orderNum.toUpperCase())) return;
    seen.add(orderNum.toUpperCase());
    entries.push({
      projectID: null,
      saleID: null,
      orderID: `__unassigned__${now}-${orderNum}`,
      vendor,
      orderPO: [orderNum, order.jobName].filter(Boolean).join(" \u00b7 "),
      ackID: null,
      source: "unassigned",
      truckNumber: truckNumber || null,
      expectedItems: order.packages || 0,
      receivedItems: 0,
      location: null,
      packingSlipDocument: null,
      packingSlipFile: null,
      missingDocument: false,
      packingSlipID: null,
      linkedOrderID: null,
      importedItems: [],
      isImporting: false,
      vendorOrderNumber: orderNum,
      notes: note(order) || "",
    });
  });
  return entries;
}

/**
 * Units of a sale item received in this session, from its scans. A label
 * read or a tap on the Expected list records one scan per unit; a panel
 * carton (`component: { index, of }`) records one per carton, and `of` of
 * them make one unit -- the fold completeReceiving applies, so a five-panel
 * door counts as one door here too and never five.
 *
 * A Quaker carton's barcode names its unit (vendorBarcode.quakerUnitKey), and
 * a unit that ships as several packages prints the same unit on each, so
 * Quaker scans count the units they name: a door's three packages are one
 * door. How many packages make the unit is not on the barcode, so the unit
 * counts from its first package.
 */
export function unitsReceivedForItem(scans, saleItemID) {
  return countUnits(
    scans,
    (scan) => Boolean(saleItemID) && scan.saleItemID === saleItemID
  );
}

/**
 * Whether a scan is a unit received against a PO but tied to none of its
 * items: a Quaker carton whose line found no item (itemOnVendorLine), or a
 * Kolbe or Windsor serial filed from the unmatched card. A screen, hardware
 * or accessory carton is not a unit the Expected list counts. The unmatched
 * card types a carton a unit unless the receiver picks another type, so for
 * a serial vendor this counts cartons: a screen box, or a unit's second box,
 * filed as it is counts as a unit.
 */
export function isUnmatchedUnitScan(scan, orderID) {
  return Boolean(
    scan &&
      orderID &&
      scan.orderID === orderID &&
      !scan.saleItemID &&
      (!scan.itemType || scan.itemType === "unit")
  );
}

/**
 * Units received against a PO in this session but tied to none of its items
 * (isUnmatchedUnitScan), counted as unitsReceivedForItem counts: a Quaker
 * unit once however many of its packages were scanned, and not at all when
 * another package of it did find its item.
 */
export function unmatchedUnitsOnOrder(scans, orderID) {
  const matchedUnits = new Set();
  (scans || []).forEach((scan) => {
    if (!scan || scan.orderID !== orderID || !scan.saleItemID) return;
    const vendorUnit = quakerUnitKey(scan.barcode);
    if (vendorUnit) matchedUnits.add(vendorUnit);
  });
  return countUnits(
    scans,
    (scan) =>
      isUnmatchedUnitScan(scan, orderID) &&
      !matchedUnits.has(quakerUnitKey(scan.barcode))
  );
}

/**
 * Screen, hardware and accessory cartons scanned against a PO in this
 * session: every carton typed anything but a unit, whether or not it found
 * an item. Counted, never compared with a quantity -- one accessory carton
 * can hold several accessory items, so how many cartons are due is unknown.
 */
export function extraCartonsOnOrder(scans, orderID) {
  return (scans || []).filter(
    (scan) =>
      scan &&
      orderID &&
      scan.orderID === orderID &&
      scan.itemType &&
      scan.itemType !== "unit"
  ).length;
}

/** The units among the scans `counts` picks: see unitsReceivedForItem. */
function countUnits(scans, counts) {
  let whole = 0;
  let panels = 0;
  let panelsPerUnit = 0;
  const vendorUnits = new Set();
  (scans || []).forEach((scan) => {
    if (!scan || !counts(scan)) return;
    const vendorUnit = quakerUnitKey(scan.barcode);
    if (vendorUnit) {
      vendorUnits.add(vendorUnit);
      return;
    }
    const of = scan.component ? Number(scan.component.of) || 0 : 0;
    if (of > 1) {
      panels++;
      panelsPerUnit = Math.max(panelsPerUnit, of);
    } else {
      whole++;
    }
  });
  return (
    whole +
    vendorUnits.size +
    (panelsPerUnit ? Math.floor(panels / panelsPerUnit) : 0)
  );
}

/**
 * The sale item a vendor's line number names on a PO: the one item of the
 * PO's vendor on that line that is not an accessory of another. Quaker's
 * LINE is the quote line the item was imported from (checked on 01800031A,
 * 03600012B and 01000004B: line, size and quantity agree), and a line's
 * accessories -- its panels, its mull kit -- share the number, so they are
 * left out. Null when the line holds no such item or more than one.
 *
 * But Quaker numbers each sales order from 1, and our line is the item's
 * place in the whole quote (Quote.setItemLines). The two agree only while
 * the vendor's items are the quote's first lines, one to a line, with
 * nothing before or between them, and came on one sales order: otherwise
 * (or when the carton's unit number is more than the item has) null, and
 * the carton is received against the PO alone. `po.linesAmbiguous` says the
 * vendor has another order or sales order on the sale, or this one is a
 * service reorder's (see lookUpQuakerOrder).
 */
export function itemOnVendorLine(saleItems, po, lineNumber, unit) {
  const line = Number(lineNumber);
  if (!po || po.linesAmbiguous || !Number.isInteger(line) || line <= 0) {
    return null;
  }
  const vendorItems = Object.values(saleItems || {}).filter(
    (item) =>
      item &&
      item.projectID === po.projectID &&
      item.saleID === po.saleID &&
      item.data.brand === po.vendor &&
      !item.data.parentID
  );
  // Screens and hardware live on line 0, outside the numbering.
  const lines = vendorItems
    .map((item) => Number(item.data.line))
    .filter((itemLine) => itemLine !== 0)
    .sort((a, b) => a - b);
  if (lines.some((itemLine, i) => itemLine !== i + 1)) return null;
  const items = vendorItems.filter((item) => Number(item.data.line) === line);
  if (items.length !== 1) return null;
  if (unit != null && Number(unit) > Number(items[0].data.quantity)) {
    return null;
  }
  return items[0];
}

/**
 * The next number for a unit received without a barcode: the minted barcode
 * carries it, so a three-of-a-kind line can be received three times instead
 * of the second being refused as a re-scan of the first.
 */
export function nextLabelSequence(scans, saleItemID) {
  return (
    (scans || []).filter((scan) => scan && scan.saleItemID === saleItemID)
      .length + 1
  );
}

/**
 * The orders a session expects PACKAGES of rather than items: every order
 * with a shipment record in view, and every order this session has matched a
 * package of. The second matters because the pending listener drops a
 * shipment the moment its last package is received -- without it, a PO whose
 * packages were all scanned off a truck the session never named would flip
 * to listing its items as still to come.
 */
export function packageListedOrderIDs(shipments = [], scans = []) {
  const ids = new Set();
  shipments.forEach((shipment) => {
    if (shipment && shipment.orderID) ids.add(shipment.orderID);
  });
  scans.forEach((scan) => {
    if (scan && scan.vendorShipmentID && scan.orderID) ids.add(scan.orderID);
  });
  return ids;
}

/**
 * Whether a session entry's Expected list is the sale's items, not packages.
 * An entry the vendor's feed placed (`source: "vendor"`) always has packages,
 * even once every shipment of it is received and out of the pending
 * listener's view; unassigned material has no sale to list. Nor does an
 * entry with no order of its own (`__service__…`, `__other__…`,
 * `__unassigned__…`): a service issue's entry names the issue's sale, but
 * that sale's windows were delivered long ago and are not on this truck.
 */
export function listsSaleItems(po, packageListed = new Set()) {
  return Boolean(
    po &&
      po.projectID &&
      po.saleID &&
      po.vendor &&
      po.source !== "vendor" &&
      !String(po.orderID || "").startsWith("__") &&
      !packageListed.has(po.orderID)
  );
}

/**
 * Whether the Expected list leaves a sale item out because no carton can
 * ever be received as it: an accessory of another item (a mull kit, a panel)
 * comes in an accessory carton that names no item, and Screens or Hardware
 * on line 0 -- outside the quote's numbering, or made by QC from an earlier
 * delivery's screen and hardware cartons -- come typed as screens and
 * hardware, never as the item.
 */
function neverReceivedAsItem(item) {
  return Boolean(
    item.data.parentID ||
      (!Number(item.data.line) &&
        (item.data.series === "Screens" || item.data.series === "Hardware"))
  );
}

/**
 * Whether a session entry's packages are accounted for on Complete: counted
 * as expected, listed missing, left to later trucks, and turned into (or
 * cleared from) missing-material issues. Decided by the vendor's feed, not by
 * how the PO was added: a Marvin PO picked by search or from a scanned
 * packing slip is entered by hand (`source: "manual"`) and stays so once the
 * truck is named, yet its packages are on the truck all the same. Material
 * with no order of its own never is.
 */
export function accountsPackages(po, packageListed = new Set()) {
  if (!po) return false;
  if (po.source === "vendor") return true;
  if (
    po.source === "unassigned" ||
    po.source === "service" ||
    po.source === "other" ||
    String(po.orderID || "").startsWith("__")
  ) {
    return false;
  }
  return packageListed.has(po.orderID);
}

/**
 * Whether Complete can tell that this delivery brought anything of a session
 * entry's order: a truck is named (its records say what), or a scan took one
 * of the order's vendor packages, or the vendor's feed placed the entry (a PO
 * chosen up front from Expected Shipments counts every record, as before --
 * see deliveryShipments). A PO picked by hand on a delivery that never named a
 * truck and matched none of its packages -- a parts box by parcel, received
 * with No Barcode -- did not bring what Marvin lists on its real trucks:
 * counting those packages missing opened a vendor-billable issue for a later
 * truck's windows (2026-10-05). They are still to come instead.
 */
export function deliversOrder(po, truckNumber, scans = []) {
  if (!po) return false;
  if (po.source === "vendor" || truckNumber) return true;
  return scans.some(
    (scan) => isVendorScan(scan) && scan.orderID === po.orderID
  );
}

/**
 * What a session expects of a PO whose vendor sends no package list --
 * Lincoln, Windsor, Kolbe, Quaker, anyone but Marvin: the sale's items of
 * that vendor, with how many units of each are received, off an earlier
 * truck or in this session. An earlier truck counts once QC has made its
 * units ready (quantityReady), not from the sale's slips: the receiving page
 * does not load the sale, so the slips would credit a PO on one handheld and
 * not on the next, and a slip's count includes a damaged unit whose
 * replacement is still to come. A PO with packages
 * (see listsSaleItems) is left out; its packages are what it expects. So is
 * an item no carton is ever received as (neverReceivedAsItem).
 *
 * A unit received against the PO alone (unmatchedUnitsOnOrder) names no
 * item, so no row can count it: it comes off the PO's `remaining` instead,
 * so the badge and the "to go" count still reach zero once every carton is
 * scanned. An item fully received drops off, and a PO with nothing left
 * drops with it, the way packages do.
 *
 * `extras` says the screen, hardware and accessory cartons are coming in:
 * `{ cartons }` scanned against the PO (extraCartonsOnOrder), set when the
 * PO has such items or such a carton was scanned, else null. It is for
 * reading only -- no row stands for those items, and it never holds the PO
 * on the list.
 *
 * Each group is `{ orderID, projectID, saleID, orderPO, vendor, items:
 * [{ item, quantity, received, remaining }], unmatched, remaining, extras }`,
 * items in line order.
 */
export function expectedItemGroups(
  selectedOrders = [],
  saleItems = {},
  shipments = [],
  scans = []
) {
  const packageListed = packageListedOrderIDs(shipments, scans);
  const groups = [];
  selectedOrders.forEach((po) => {
    if (!listsSaleItems(po, packageListed)) return;
    const vendorItems = Object.values(saleItems).filter(
      (item) =>
        item &&
        item.projectID === po.projectID &&
        item.saleID === po.saleID &&
        item.data.brand === po.vendor
    );
    const items = sortBy(
      vendorItems.filter((item) => !neverReceivedAsItem(item)),
      [
        (item) => Number(item.data.line) || 0,
        (item) => Number(item.data.accessoryIndex) || 0,
      ]
    );
    const entries = [];
    items.forEach((item) => {
      const quantity = Number(item.data.quantity) || 0;
      const earlier = Number(item.data.quantityReady) || 0;
      const received = Math.min(
        quantity,
        earlier + unitsReceivedForItem(scans, item.id)
      );
      const remaining = Math.max(quantity - received, 0);
      if (remaining > 0) entries.push({ item, quantity, received, remaining });
    });
    const unmatched = unmatchedUnitsOnOrder(scans, po.orderID);
    const remaining = Math.max(
      entries.reduce((sum, entry) => sum + entry.remaining, 0) - unmatched,
      0
    );
    if (!remaining) return;
    const cartons = extraCartonsOnOrder(scans, po.orderID);
    groups.push({
      orderID: po.orderID,
      projectID: po.projectID,
      saleID: po.saleID,
      orderPO: po.orderPO,
      vendor: po.vendor,
      items: entries,
      unmatched,
      remaining,
      extras:
        cartons || vendorItems.some(neverReceivedAsItem) ? { cartons } : null,
    });
  });
  return groups;
}

/** A Firestore Timestamp or a Date, in ms; 0 when there is none. */
function millis(at) {
  if (!at) return 0;
  if (typeof at.toMillis === "function") return at.toMillis();
  const time = new Date(at).getTime();
  return Number.isNaN(time) ? 0 : time;
}

/** When a scan was recorded, in ms: a Firestore Timestamp, a Date, or 0. */
function scanTime(scan) {
  return millis(scan && scan.scannedAt);
}

/**
 * A session's scans in the order they were recorded. The page lists them
 * newest first, and a slip built in that order put its LAST location first --
 * the zone QC starts every item at.
 */
export function inScanOrder(scans = []) {
  return sortBy(scans.filter(Boolean), scanTime);
}

/**
 * A scan's location as a packing slip records it: the scanner's
 * "warehouse:zone" or "warehouse:zone:bin" split into the zone and its bin.
 */
export function slipLocation(location, itemType, quantity) {
  const parts = String(location || "").split(":");
  return {
    id: parts.length >= 2 ? `${parts[0]}:${parts[1]}` : parts[0] || "",
    bin: parts.length >= 3 ? parseInt(parts[2]) || null : null,
    type: "warehouse",
    itemType,
    quantity,
  };
}

/**
 * The vendor package a scan received, found by its tracking number on the
 * shipment it matched. When that shipment no longer lists it -- out of view,
 * received in full so the pending listener dropped it, or retired by the
 * sync once Marvin moved its load onto a real truck, or refreshed in place
 * with only the part of the load Marvin left on it -- another record of the
 * same order carrying the package stands in (preferredShipment): its line
 * names the same sale item. Without it the 25 scans Justin's dock received
 * against the placeholder copy (2026-10-05) put no item on their slip. The
 * line and package indices recorded with the scan are the last resort, for a
 * scan that recorded a piece-marked label rather than its package's number,
 * and only when the package at them is that one (codesMatch): a refresh
 * re-sends the lines whole, and another package can sit there now.
 */
function scannedPackage(scan, shipments) {
  if (!scan.vendorShipmentID) return null;
  const shipment = shipments.find(
    (candidate) => candidate && candidate.id === scan.vendorShipmentID
  );
  const found = packageOn(shipment, scan.barcode);
  if (found) return found;
  const copy = preferredShipment(
    shipments.filter(
      (candidate) =>
        candidate &&
        candidate !== shipment &&
        candidate.orderID === scan.orderID &&
        packageOn(candidate, scan.barcode)
    )
  );
  if (copy) return packageOn(copy, scan.barcode);
  if (!shipment) return null;
  const lines = shipment.data.lines || [];
  const line = lines[scan.vendorLineIndex];
  const trackingLine =
    line && (line.trackingLines || [])[scan.vendorTrackingIndex];
  return trackingLine && codesMatch(scan.barcode, trackingLine.trackingNo)
    ? { line, trackingLine }
    : null;
}

/**
 * The sale item a scan received, when anything says which. A label read or a
 * tap on an item row records it outright. A vendor package names it through
 * the shipment line it resolved to -- but only a unit package of the line
 * itself: Marvin's "1a" screens and "1b" casing carry line 1's item
 * (isComponent), and receiving them is not receiving the window. Null for
 * anything else: an unmatched carton, a packing-slip line, a package the
 * vendor's feed never tied to an item.
 */
export function scanSaleItemID(scan, shipments = []) {
  if (!scan) return null;
  if (scan.saleItemID) return scan.saleItemID;
  const pkg = scannedPackage(scan, shipments);
  if (!pkg || !pkg.line.saleItemID || pkg.line.isComponent) return null;
  return (scan.itemType || "unit") === "unit" ? pkg.line.saleItemID : null;
}

/**
 * Where a session put each sale item it received, as a packing slip's
 * per-item `items` map: `{ [saleItemID]: { ready: 0, locations } }`. QC
 * starts each item at these locations; without them it starts every item at
 * whichever location the slip lists first.
 *
 * Each location holds the units scanned to it, in the order they came off the
 * truck. A vendor package counts its pieces (a parts carton of 200 fasteners
 * is 200). The total is capped at the units received, counted the way the
 * Expected list counts them (unitsReceivedForItem: five panel cartons are one
 * door), and at what the item still has to be made ready: a unit that ships
 * as three packages is still one unit, and a location past the cap stays
 * listed at zero so QC can see where the rest of it went. `ready` stays 0 --
 * receiving says where the material is; QC still says it is good.
 *
 * `saleItems` maps id -> SaleItem. A scan whose item is not in it is left
 * out, and QC places that item from the slip's pooled locations as before.
 */
export function receivedItemLocations(
  scans = [],
  shipments = [],
  saleItems = {}
) {
  const byItem = new Map();
  // Pieces of vendor packages per item; unitsReceivedForItem counts the rest.
  const pieces = new Map();
  // Units are credited where they went the way unitsReceivedForItem counts
  // them: a Quaker unit once, at its first package, and a panel door from its
  // panels. Crediting every scan as a unit let the first location soak up the
  // whole capped total, so two units put in two places both opened at the
  // first.
  const vendorUnits = new Map();
  const panelsPerUnit = new Map();
  inScanOrder(scans).forEach((scan) => {
    const itemID = scanSaleItemID(scan, shipments);
    if (!itemID || !saleItems[itemID] || !scan.location) return;
    const pkg = scan.saleItemID ? null : scannedPackage(scan, shipments);
    const vendorUnit = pkg ? null : quakerUnitKey(scan.barcode);
    const of = !pkg && scan.component ? Number(scan.component.of) || 0 : 0;
    let units = (pkg && Number(pkg.trackingLine.trackingQty)) || 1;
    if (pkg) pieces.set(itemID, (pieces.get(itemID) || 0) + units);
    if (vendorUnit) {
      if (!vendorUnits.has(itemID)) vendorUnits.set(itemID, new Set());
      const seen = vendorUnits.get(itemID);
      units = seen.has(vendorUnit) ? 0 : 1;
      seen.add(vendorUnit);
    } else if (of > 1) {
      units = 0;
      panelsPerUnit.set(itemID, Math.max(panelsPerUnit.get(itemID) || 0, of));
    }
    const { id, bin } = slipLocation(scan.location);
    const key = `${id};${bin || ""}`;
    if (!byItem.has(itemID)) byItem.set(itemID, new Map());
    const locations = byItem.get(itemID);
    const entry = locations.get(key) || {
      location: scan.location,
      units: 0,
      panels: 0,
    };
    entry.units += units;
    if (!vendorUnit && of > 1) entry.panels++;
    locations.set(key, entry);
  });

  // Each location holds the doors its panels make up. A door whose panels
  // were split goes where most of its panels are: the location with the most
  // panels left over.
  panelsPerUnit.forEach((perUnit, itemID) => {
    const entries = [...byItem.get(itemID).values()];
    let doors = Math.floor(
      entries.reduce((total, entry) => total + entry.panels, 0) / perUnit
    );
    entries.forEach((entry) => {
      entry.units += Math.floor(entry.panels / perUnit);
      doors -= Math.floor(entry.panels / perUnit);
    });
    sortBy(entries, (entry) => -(entry.panels % perUnit))
      .slice(0, doors)
      .forEach((entry) => entry.units++);
  });

  const items = {};
  byItem.forEach((locations, itemID) => {
    const item = saleItems[itemID];
    const outstanding =
      (Number(item.data.quantity) || 0) -
      (Number(item.data.quantityReady) || 0);
    const received =
      unitsReceivedForItem(scans, itemID) + (pieces.get(itemID) || 0);
    let left = Math.max(0, Math.min(outstanding, received));
    if (!left) return;
    items[itemID] = {
      ready: 0,
      locations: [...locations.values()].map(({ location, units }) => {
        const quantity = Math.min(units, left);
        left -= quantity;
        return slipLocation(location, item.itemLocationItemType, quantity);
      }),
    };
  });
  return items;
}

/**
 * The units of each sale item a packing slip's receiving step put somewhere,
 * from the slip's per-item `items` map: `{ [saleItemID]: units }`. A truck's
 * Complete (receivedItemLocations) and Vendor Receiving Mode both write that
 * map before QC, each unit counted once at the location it went to. A slip
 * entered by hand has no map until QC writes its report, so it records
 * nothing here. Items with nothing placed are left out.
 */
export function slipReceivedUnits(items = {}) {
  const received = {};
  Object.entries(items || {}).forEach(([itemID, entry]) => {
    const units = ((entry && entry.locations) || []).reduce(
      (total, location) => total + (Number(location && location.quantity) || 0),
      0
    );
    if (units > 0) received[itemID] = units;
  });
  return received;
}

/**
 * How many of a sale item have come off a truck, whether or not QC has seen
 * them yet. QC is what makes material ready, so between a truck being saved
 * and its slip being QC'd the item's `quantityReady` says nothing about the
 * receipt; the `received` map each slip's entry in the sale's status carries
 * (slipReceivedUnits) does. A QC'd slip's units are in `quantityReady`, but
 * what receiving recorded still counts when QC passed fewer -- a damaged
 * window was still delivered to the dock. Slips with no map (entered by hand,
 * or saved before the map existed) count once QC makes them ready. Entries
 * whose order is gone are ignored, as computeSaleStatus ignores them. Capped
 * at the item's quantity.
 *
 * @param {string} itemID
 * @param {{quantity:number, quantityReady:number}} item - the item's data
 * @param {?object} saleStatus - the sale's `status` map, or null when the
 *   sale is not loaded (the answer is then what QC made ready)
 */
export function quantityReceived(itemID, item, saleStatus) {
  const quantity = Number(item && item.quantity) || 0;
  const ready = Number(item && item.quantityReady) || 0;
  const orders = (saleStatus && saleStatus.orders) || {};
  let recorded = 0;
  let awaitingQC = 0;
  Object.values((saleStatus && saleStatus.packingSlips) || {}).forEach(
    (slip) => {
      if (!slip || !slip.orderID || !(slip.orderID in orders)) return;
      const units = Number(slip.received && slip.received[itemID]) || 0;
      recorded += units;
      if (!slip.qcInspected) awaitingQC += units;
    }
  );
  return Math.min(quantity, Math.max(ready + awaitingQC, recorded));
}
